import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/format";
import { limitationStatus, LIMITATION_WINDOW_DAYS, LIMITATION_COLORS } from "@/lib/limitation";
import { AlertTriangle, ShieldCheck } from "lucide-react";

// Limitation assessment mark: a 3-year (1095-day) limitation window rendered as
// a clock. Triggered by "Check Limitation", it runs the countdown sweep once and
// then SETTLES into a static result — days remaining, expiry date, status band
// and a critical-window warning. It never loops. Reduced-motion safe.
// Status thresholds come from the shared limitationStatus() single source.
const INK = "#20242A", MUTE = "#7b8798", TINT = "#F7F5F1";
const OK = LIMITATION_COLORS.Safe, BAD = LIMITATION_COLORS.Expired;
const WINDOW = LIMITATION_WINDOW_DAYS;
const CX = 160, CY = 150, R = 92;
const polar = (deg, r) => { const a = ((deg - 90) * Math.PI) / 180; return [CX + Math.cos(a) * r, CY + Math.sin(a) * r]; };

export function LimitationClockMark({ limitationDate, daysLeft, verified = false, className }) {
  const [p, setP] = useState(0);
  const [days, setDays] = useState(WINDOW);
  const [done, setDone] = useState(false);

  const status = limitationStatus(limitationDate, { verified }) || { band: daysLeft == null ? "—" : daysLeft < 0 ? "Expired" : daysLeft <= 30 ? "Critical" : daysLeft <= 180 ? "Approaching" : "Safe", color: OK, basis: verified ? "Verified" : "Provisional" };
  const expired = status.band === "Expired";
  const critical = status.band === "Critical";
  const warn = status.band === "Approaching";
  const color = status.color || OK;
  const targetP = daysLeft == null ? 0 : Math.max(0, Math.min(1, (WINDOW - daysLeft) / WINDOW));

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion:reduce)").matches;
    if (reduce) { setP(targetP); setDays(daysLeft ?? 0); setDone(true); return; }
    let raf, start;
    const dur = 1200;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (ts) => {
      if (!start) start = ts;
      const t = Math.min(1, (ts - start) / dur);
      const e = ease(t);
      setP(targetP * e);
      setDays(Math.round(WINDOW + ((daysLeft ?? 0) - WINDOW) * e));
      if (t < 1) raf = requestAnimationFrame(step);
      else { setP(targetP); setDays(daysLeft ?? 0); setDone(true); }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [limitationDate, daysLeft, targetP]);

  const circ = 2 * Math.PI * R;
  const dash = `${(1 - p) * circ} ${circ}`;
  const [hx, hy] = polar(p * 360, R - 6);
  const dayLabel = expired ? "Expired" : daysLeft == null ? "—" : Math.max(0, Math.round(days));
  const band = expired ? "Expired" : critical ? "Critical" : warn ? "Approaching" : "Safe";

  return (
    <div className={cn("flex items-center gap-4", className)} data-testid="limitation-clock-mark">
      <svg viewBox="0 0 320 300" className="h-auto w-full max-w-[84px] shrink-0" role="img" aria-label="Limitation period assessment">
        <circle cx={CX} cy={CY} r={R + 13} fill={TINT} stroke={INK} strokeWidth="0.75" />
        {Array.from({ length: 36 }).map((_, k) => {
          const major = k % 9 === 0;
          const [x1, y1] = polar(k * 10, R + 13);
          const [x2, y2] = polar(k * 10, R + (major ? 25 : 19));
          return <line key={k} x1={x1} y1={y1} x2={x2} y2={y2} stroke={INK} strokeWidth={major ? 1 : 0.5} />;
        })}
        <circle cx={CX} cy={CY} r={R} fill="none" stroke={MUTE} strokeWidth="8" opacity="0.22" />
        <circle cx={CX} cy={CY} r={R} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" strokeDasharray={dash} transform={`rotate(-90 ${CX} ${CY})`} />
        <line x1={CX} y1={CY} x2={hx} y2={hy} stroke={INK} strokeWidth="1.5" strokeLinecap="round" />
        <circle cx={CX} cy={CY} r="5" fill={INK} />
        <text x={CX} y={CY + 6} textAnchor="middle" fontFamily="Georgia,serif" fontSize="34" fontWeight="600" fill={color}>{dayLabel}</text>
        <text x={CX} y={CY + 30} textAnchor="middle" fontFamily="Georgia,serif" fontSize="14" fill={MUTE}>{expired ? "lapsed" : "days left"}</text>
      </svg>
      <div className="min-w-0 flex-1 space-y-1.5 text-sm" data-testid="limitation-result">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-[11px] uppercase tracking-wide text-ink-muted">Expiry</span>
          <span className="font-display text-base font-semibold text-ink" data-testid="limitation-expiry-date">{formatDate(limitationDate)}</span>
          <span className="inline-flex items-center gap-1 font-medium" style={{ color }} data-testid="limitation-status">
            <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />{band}
          </span>
        </div>
        {(expired || critical) && (
          <div className="flex items-start gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium" style={{ borderColor: BAD, color: BAD, background: "rgba(168,83,83,0.08)" }} data-testid="limitation-warning">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{expired ? "Limitation has lapsed — seek condonation of delay urgently." : "File before expiry — critical window."}</span>
          </div>
        )}
        {done && !expired && !critical && (
          <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: OK }} data-testid="limitation-ok">
            <ShieldCheck className="h-3.5 w-3.5" /> Within limitation period.
          </div>
        )}
        <div className="text-[10px] text-ink-muted" data-testid="limitation-basis">
          {status.basis === "Verified" ? "Basis: rule-derived" : "Basis: provisional — manually entered, not legally determined"}
        </div>
      </div>
    </div>
  );
}
